/**-------------------------------  Copyright ---------------------------------
This software has been developed by Orange Labs TGI/OLS/SOFT
Copyright (c) Orange Labs 2018

COPYRIGHT    : This file is the property of Orange Labs.
Copyright Orange Labs 2018, All Rights Reserved

This software is the confidential and proprietary information of Orange Labs.

You shall not disclose such Confidential Information and shall use it only
in accordance with the terms of the license agreement you entered into with
Orange Labs.

AUTHORS      : Orange Labs  TGI/OLS/SOFT
EMAIL        : soft.cybersec@orange.com
-----------------------------------------------------------------------------*/


import {Component, Prop, Vue} from 'vue-property-decorator'
import {AnalysisVO} from '@/valueObjects/AnalysisVO'
import AnalysisServiceMixin from '@/services/AnalysisServiceMixin'
import ReportValidator from '@/utils/validators/ReportValidator'
import {INVALID_VALUE_GUI_MARK} from '@/utils/constants'

@Component({
    mixins: [AnalysisServiceMixin],
})
export default class AnalysisMenu extends Vue {
    @Prop()
    private analysis: AnalysisVO

    @Prop()
    private isFinished: boolean

    private hasInformation(): boolean {
        return ReportValidator.hasStaticField(this.analysis, 'md5')
    }

    private get manifest(): string {
        if (! ReportValidator.hasStaticField(this.analysis, 'manifest_analysis')) {
            return INVALID_VALUE_GUI_MARK
        }
        return this.analysis.staticReport.manifest_analysis.length.toString()
    }

    private get code_analysis(): string {
        if (! ReportValidator.hasStaticField(this.analysis, 'code_analysis')) {
            return INVALID_VALUE_GUI_MARK
        }
        return Object.keys(this.analysis.staticReport.code_analysis).length.toString()
    }

    private get activities(): string {
        if (! ReportValidator.hasStaticField(this.analysis, 'activities')) {
            return INVALID_VALUE_GUI_MARK
        }
        return this.analysis.staticReport.activities.length.toString()
    }

    private get browsableActivities(): string {
        if (! ReportValidator.hasStaticField(this.analysis, 'browsable_activities')) {
            return INVALID_VALUE_GUI_MARK
        }
        return Object.keys(this.analysis.staticReport.browsable_activities).length.toString()
    }

    private get permissions(): string {
        if (! ReportValidator.hasStaticField(this.analysis, 'permissions')) {
            return INVALID_VALUE_GUI_MARK
        }

        return Object.keys(this.analysis.staticReport.permissions).length.toString()
    }

    private get virusTotal(): string {
        if ( !ReportValidator.hasStaticField(this.analysis, 'virustotal_viruses') && !ReportValidator.hasStaticField(this.analysis, 'pfav_viruses')){
            return INVALID_VALUE_GUI_MARK
        }
        if (this.analysis.virustotalStatus === 'done') {
            return this.analysis.staticReport.virustotal_viruses.length.toString()
        }
        if (this.analysis.pfavStatus === 'done') {
            return this.analysis.staticReport.pfav_viruses.length.toString()
        }
        return INVALID_VALUE_GUI_MARK
    }

    private get providers(): string {
        if (! ReportValidator.hasStaticField(this.analysis, 'providers')) {
            return INVALID_VALUE_GUI_MARK
        }
        return this.analysis.staticReport.providers.length.toString()
    }

    private get receivers(): string {
        if (! ReportValidator.hasStaticField(this.analysis, 'receivers')) {
            return INVALID_VALUE_GUI_MARK
        }
        return this.analysis.staticReport.receivers.length.toString()
    }

    private get services(): string {
        if (! ReportValidator.hasStaticField(this.analysis, 'services')) {
            return INVALID_VALUE_GUI_MARK
        }
        return this.analysis.staticReport.services.length.toString()
    }

    private get api(): string {
        if (! ReportValidator.hasStaticField(this.analysis, 'android_api')) {
            return INVALID_VALUE_GUI_MARK
        }
        return Object.keys(this.analysis.staticReport.android_api).length.toString()
    }

    private get screenshots(): string {
        if (! ReportValidator.hasDynamicField(this.analysis, 'screenshots')) {
            return INVALID_VALUE_GUI_MARK
        }
        return this.analysis.dynamicReport.screenshots.length.toString()
    }

    private get memdumps(): string {
        if (! ReportValidator.hasDynamicField(this.analysis, 'memory_dumps')) {
            return INVALID_VALUE_GUI_MARK
        }
        return this.analysis.dynamicReport.memory_dumps.length.toString()
    }

    private get networkActivities(): string {
        if (! ReportValidator.hasDynamicField(this.analysis, 'nb_network_hosts')) {
            return INVALID_VALUE_GUI_MARK
        }
        return this.analysis.dynamicReport.nb_network_hosts
    }

    private get signatures(): string {
        if (! ReportValidator.hasStaticField(this.analysis, 'yara_matches')
            && ! ReportValidator.hasDynamicField(this.analysis, 'yara_matches')) {
            return INVALID_VALUE_GUI_MARK
        }
        var list=[]
        for(const regleYara in this.analysis.signatures){
            list.push(this.analysis.signatures[regleYara].rulesetName)
        }
        let diffruleset = new Set(list)
        return ((new Set(list)).size).toString()
    }

    private isInvalid(fieldValue: string): boolean {
        return fieldValue === INVALID_VALUE_GUI_MARK
    }

    private get hasLogs(): boolean {
        return ReportValidator.hasDynamicField(this.analysis, 'syslog')
    }

    private get hasHook(): boolean {
        return ReportValidator.hasDynamicField(this.analysis, 'hook')
    }

    private get smses(): string {
        if (! ReportValidator.hasDynamicField(this.analysis, 'smses')) {
            return INVALID_VALUE_GUI_MARK
        }
        return this.analysis.dynamicReport.smses.length.toString()
    }

    private get calls(): string {
        if (! ReportValidator.hasDynamicField(this.analysis, 'phone_calls')) {
            return INVALID_VALUE_GUI_MARK
        }
        return this.analysis.dynamicReport.phone_calls.length.toString()
    }

    private get isInteractiveAnalysis(): boolean {
        return this.analysis.isInteractive
    }

    private get interactiveSessionNotAvailable(): boolean {
        return  this.analysis.dynamicAnalysisIsFinished
                || this.analysis.interactiveConnection === ''
    }
}
